import React, { useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import Hls from "hls.js";

const RETRY_DELAY_MS = 2000;
const MAX_RETRIES    = 8;

const LiveHLS = forwardRef(({ url, onReady }, ref) => {
  const videoRef   = useRef(null);
  const hlsRef     = useRef(null);
  const retryTimer = useRef(null);
  const retryCount = useRef(0);
  const started    = useRef(false);

  const clearRetryTimer = () => {
    if (retryTimer.current) {
      clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }
  };

  const destroyHls = () => {
    clearRetryTimer();
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
  };

  const initHls = () => {
    const video = videoRef.current;
    if (!video || !url) return;

    destroyHls(); // clean slate on every (re)attempt

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari native HLS
      video.src = url;
      video.load();
      video.addEventListener("loadedmetadata", () => {
        video.play().catch(() => {});
        onReady?.();
      }, { once: true });
      return;
    }

    if (!Hls.isSupported()) return;

    const hls = new Hls({
      lowLatencyMode:      false,
      maxBufferLength:     30,
      liveSyncDurationCount: 3,
      // Back off quickly while segments aren't ready yet
      manifestLoadingMaxRetry:       0,   // we handle retries ourselves
      levelLoadingMaxRetry:          0,
      fragLoadingMaxRetry:           0,
    });

    hlsRef.current = hls;
    hls.loadSource(url);
    hls.attachMedia(video);

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      retryCount.current = 0; // reset on success
      video.play().catch(() => {});
      onReady?.();
    });

    hls.on(Hls.Events.ERROR, (_, data) => {
      if (!data.fatal) return;

      console.warn(`[LiveHLS] fatal error (attempt ${retryCount.current + 1}):`, data.type, data.details);

      destroyHls();

      if (retryCount.current < MAX_RETRIES) {
        retryCount.current += 1;
        // Exponential backoff: 2s, 2s, 4s, 4s, …  capped at 8s
        const delay = retryCount.current <= 2
          ? RETRY_DELAY_MS
          : Math.min(RETRY_DELAY_MS * Math.pow(2, retryCount.current - 2), 8000);

        console.log(`[LiveHLS] retrying in ${delay}ms…`);
        retryTimer.current = setTimeout(initHls, delay);
      } else {
        console.error("[LiveHLS] max retries reached, giving up.");
      }
    });
  };

  useImperativeHandle(ref, () => ({
    start() {
      if (started.current) return;
      started.current = true;
      retryCount.current = 0;
      initHls();
    },
  }));

  // Cleanup on unmount / modal close
  useEffect(() => {
    return () => {
      destroyHls();
      started.current = false;
    };
  }, []);

  return (
    <video
      ref={videoRef}
      muted
      playsInline
      controls={false}
      className="w-full h-full object-contain bg-black"
    />
  );
});

export default LiveHLS;